import React from 'react';
import { ActionButton, type ActionButtonData } from '../action-button/ActionButton';
import { UndoActionButton, type UndoActionButtonData } from '../undo-action-button/UndoActionButton';
import { t } from '../../services/locale';
import type { ProfileCredit, ProfileDateFormatters, ProfileMembershipBranch, ProfileStackedMembership, ProfileUser } from './profileMembership.types';
import {
  getMembershipExpiryDate,
  getMembershipPauseEndRow,
  getMembershipPauseStartRow,
  getMembershipStartDate,
  getMembershipStatusTag,
  getSubscriptionNextPayment,
  hasMembershipPauseRows,
  isCancelAllowed,
  isPauseAllowed,
  memberIsPAYGOrCredits,
  membershipAlreadyExpired,
  membershipIsSubscription,
  showContactStudioAboutMembership,
  showManageButton,
  showManageButtonForAnyMembership,
  type MembershipActionContext,
} from './profileMembershipRules';
import { getCreditExpiryDate, getCreditStartDate } from './profileCreditRules';

export type ProfileCreditRow = ProfileCredit & {
  available?: number;
  model?: string;
};

export type ProfileMembershipListData = {
  stackedMemberships?: ProfileStackedMembership[];
  user?: ProfileUser | null;
  branch?: ProfileMembershipBranch | null;
  formatters?: ProfileDateFormatters;
  /** Today's `YYYY-MM-DD` in the branch timezone. */
  todayLocal?: string;
  /** `is-click-to-cancel-email-request-enabled`. */
  isClickToCancelEnabled?: boolean;
  requestedMembershipCancellationAlertText?: string;
  creditsAvailable?: number | null;
  credits?: ProfileCreditRow[];
  mailToContactStudio?: string;
  getPauseActionData?: (membership: ProfileStackedMembership) => ActionButtonData;
  getCancelActionData?: (membership: ProfileStackedMembership) => ActionButtonData;
  getManageActionData?: (membership: ProfileStackedMembership) => ActionButtonData;
  buyMembershipActionData?: ActionButtonData;
  backToPaygActionData?: UndoActionButtonData;
  onPause?: (membership: ProfileStackedMembership) => void;
  onCancelMembership?: (membership: ProfileStackedMembership) => void;
  onManage?: (membership: ProfileStackedMembership) => void;
  onBuyMembership?: () => void;
  onTrackContactStudio?: () => void;
};

type Props = Readonly<{
  data?: ProfileMembershipListData;
}>;

const emptyFormatters: ProfileDateFormatters = {
  formatUnix: () => '',
  formatLocalDate: () => '',
};

function PauseIcon() {
  return (
    <span className="pill-pause-icon" aria-hidden="true">
      <svg width="12" height="12" viewBox="0 0 12 12" focusable="false">
        <rect x="2" y="2" width="2.5" height="8" rx="0.5" fill="currentColor" />
        <rect x="7.5" y="2" width="2.5" height="8" rx="0.5" fill="currentColor" />
      </svg>
    </span>
  );
}

function MembershipStatusPill({ status }: Readonly<{ status: string }>) {
  if (!status) {
    return null;
  }

  return (
    <div className={`pill ${getMembershipStatusTag(status)}`.trim()}>
      {status === 'PAUSED' ? <PauseIcon /> : null}
      {t(status)}
    </div>
  );
}

function CancellationWarning({ message }: Readonly<{ message?: string }>) {
  return (
    <div className="warning">
      <svg xmlns="http://www.w3.org/2000/svg" width="30" height="30" viewBox="0 0 24 24" fill="none">
        <path
          d="M11 7H13V9H11V7ZM11 11H13V17H11V11ZM12 2C6.48 2 2 6.48 2 12C2 17.52 6.48 22 12 22C17.52 22 22 17.52 22 12C22 6.48 17.52 2 12 2ZM12 20C7.59 20 4 16.41 4 12C4 7.59 7.59 4 12 4C16.41 4 20 7.59 20 12C20 16.41 16.41 20 12 20Z"
          fill="#1793e5"
        />
      </svg>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
        <p className="warning__message bold">{t('WEB_CANCEL_MEMBERSHIP_FTC_REQUESTED_ALERT_TITLE')}</p>
        <p className="warning__message">{message}</p>
      </div>
    </div>
  );
}

function MembershipPauseRows({
  membership,
  branch,
  formatters,
}: Readonly<{
  membership: ProfileStackedMembership;
  branch: ProfileMembershipBranch | null | undefined;
  formatters: ProfileDateFormatters;
}>) {
  if (!hasMembershipPauseRows(membership)) {
    return null;
  }

  return (
    <div>
      <p>{getMembershipPauseStartRow(membership, branch, formatters)}</p>
      <p>{getMembershipPauseEndRow(membership, branch, formatters)}</p>
    </div>
  );
}

function FullWidthAction({ data }: Readonly<{ data: ActionButtonData }>) {
  return (
    <span className="profile-action-button action-button-full-width">
      <ActionButton data={data} />
    </span>
  );
}

function buildRowAction(
  membership: ProfileStackedMembership,
  allowed: boolean,
  getData: ((membership: ProfileStackedMembership) => ActionButtonData) | undefined,
  onClick: () => void,
): ActionButtonData | undefined {
  if (!allowed || !getData) {
    return undefined;
  }
  const action = getData(membership);
  if (!action) {
    return undefined;
  }
  return { ...action, onClick };
}

function rowActions(
  membership: ProfileStackedMembership,
  data: ProfileMembershipListData,
  context: MembershipActionContext,
): ActionButtonData[] {
  const manage = buildRowAction(
    membership,
    showManageButton(membership, context),
    data.getManageActionData,
    () => data.onManage?.(membership),
  );
  if (manage) {
    return [manage];
  }

  return [
    buildRowAction(
      membership,
      isPauseAllowed(membership),
      data.getPauseActionData,
      () => data.onPause?.(membership),
    ),
    buildRowAction(
      membership,
      isCancelAllowed(membership, context),
      data.getCancelActionData,
      () => data.onCancelMembership?.(membership),
    ),
  ].filter((action): action is ActionButtonData => !!action);
}

function MembershipRowActions({
  membership,
  data,
  context,
}: Readonly<{
  membership: ProfileStackedMembership;
  data: ProfileMembershipListData;
  context: MembershipActionContext;
}>) {
  return (
    <>
      {rowActions(membership, data, context).map((action) => (
        <FullWidthAction key={action.dataTestId ?? action.label} data={action} />
      ))}
    </>
  );
}

function MembershipRow({
  membership,
  index,
  data,
  context,
  formatters,
}: Readonly<{
  membership: ProfileStackedMembership;
  index: number;
  data: ProfileMembershipListData;
  context: MembershipActionContext;
  formatters: ProfileDateFormatters;
}>) {
  const stacked = data.stackedMemberships;
  const showCancellationWarning = !!(
    data.isClickToCancelEnabled && membership.isCancellationRequested
  );

  return (
    <li>
      <div className="list-text-left">
        <h4 id={`membership-name-label-${index}`}>
          {membership.membershipMetaData?.membershipName}{' '}
          <MembershipStatusPill status={membership.status ?? ''} />
        </h4>
        <p id={`membership-plan-name-label-${index}`} className="bold">
          {membership.membershipMetaData?.planName}
        </p>
        <p>{getMembershipStartDate(membership, stacked, data.branch, formatters.formatUnix)}</p>
        <p>{getMembershipExpiryDate(membership, data.branch, formatters.formatUnix)}</p>
        <p>{getSubscriptionNextPayment(membership, data.branch, formatters)}</p>
        <MembershipPauseRows
          membership={membership}
          branch={data.branch}
          formatters={formatters}
        />
        {showCancellationWarning ? (
          <CancellationWarning message={data.requestedMembershipCancellationAlertText} />
        ) : (
          <MembershipRowActions membership={membership} data={data} context={context} />
        )}
      </div>
    </li>
  );
}

function creditLabel(credit: ProfileCreditRow): string {
  const modelKey = `${(credit.model ?? '').toUpperCase()}_CREDITS`;
  return `${credit.available ?? ''} ${t(modelKey)}`.trim();
}

function CreditsSection({
  credits,
  branch,
  formatters,
}: Readonly<{
  credits: ProfileCreditRow[];
  branch: ProfileMembershipBranch | null | undefined;
  formatters: ProfileDateFormatters;
}>) {
  return (
    <>
      <div className="list-label">{t('WEB_CREDITS_DETAILS')}</div>
      <ul className="list">
        {credits.map((credit, index) => (
          <li key={`${credit.model ?? 'credit'}-${index}`}>
            <div className="list-text-left">
              <h4>{creditLabel(credit)}</h4>
              <p>
                {getCreditStartDate(credit, branch, formatters.formatUnix)}{' '}
                {getCreditExpiryDate(credit, branch, formatters.formatUnix)}
              </p>
            </div>
          </li>
        ))}
      </ul>
    </>
  );
}

function MembershipPrimaryActions({
  data,
  showBuy,
  showBackToPayg,
}: Readonly<{
  data: ProfileMembershipListData;
  showBuy: boolean;
  showBackToPayg: boolean;
}>) {
  return (
    <>
      {showBuy && data.buyMembershipActionData ? (
        <span className="profile-action-button">
          <ActionButton
            data={{
              ...data.buyMembershipActionData,
              onClick: () => data.onBuyMembership?.(),
            }}
          />
        </span>
      ) : null}
      {showBackToPayg && data.backToPaygActionData ? (
        <span className="profile-action-button">
          <UndoActionButton data={data.backToPaygActionData} />
        </span>
      ) : null}
    </>
  );
}

function ContactStudioLink({
  href,
  onTrack,
}: Readonly<{
  href?: string;
  onTrack?: () => void;
}>) {
  return (
    <a
      className="contact-studio"
      href={href}
      target="_blank"
      rel="noreferrer"
      onClick={() => onTrack?.()}
    >
      <p>{t('WEB_CONTACT_STUDIO')}</p>
      <i className="icon icon-arrow-right" />
    </a>
  );
}

function resolveTabFlags(
  data: ProfileMembershipListData,
  stacked: ProfileStackedMembership[],
  context: MembershipActionContext,
  formatters: ProfileDateFormatters,
) {
  const showBuy = memberIsPAYGOrCredits(data.user);
  const showBackToPayg = !showBuy
    && membershipAlreadyExpired(data.user, data.todayLocal ?? '', formatters.formatUnix)
    && !membershipIsSubscription(data.user);
  const showCredits = data.creditsAvailable != null && data.creditsAvailable > 0;
  const showContact = !showManageButtonForAnyMembership(stacked, context)
    && context.isContactStudioEnabled;

  return { showBuy, showBackToPayg, showCredits, showContact };
}

export function ProfileMembershipList({ data }: Props) {
  if (!data) {
    return null;
  }

  const formatters = data.formatters ?? emptyFormatters;
  const context: MembershipActionContext = {
    isClickToCancelEnabled: !!data.isClickToCancelEnabled,
    isContactStudioEnabled: showContactStudioAboutMembership(data.user),
  };
  const stacked = data.stackedMemberships ?? [];
  const { showBuy, showBackToPayg, showCredits, showContact } = resolveTabFlags(
    data,
    stacked,
    context,
    formatters,
  );

  return (
    <>
      <div className="list-label">{t('WEB_MEMBERSHIP_DETAILS')}</div>
      <ul className="list">
        {stacked.map((membership, index) => (
          <MembershipRow
            key={membership.id ?? index}
            membership={membership}
            index={index}
            data={data}
            context={context}
            formatters={formatters}
          />
        ))}
      </ul>
      <MembershipPrimaryActions
        data={data}
        showBuy={showBuy}
        showBackToPayg={showBackToPayg}
      />
      {showCredits ? (
        <CreditsSection
          credits={data.credits ?? []}
          branch={data.branch}
          formatters={formatters}
        />
      ) : null}
      {showContact ? (
        <ContactStudioLink
          href={data.mailToContactStudio}
          onTrack={data.onTrackContactStudio}
        />
      ) : null}
    </>
  );
}
